import type { AreaBoundary, ViewState } from '../types/warehouse.types';
import { calculatePolygonCentroid, drawTextWithBackground } from '../utils/geometry';
import { Theme } from '@material-ui/core';
import { drawPolygon } from '../utils/canvasDrawing';
import { BaseRenderer } from './base/BaseRenderer';

export interface AreaRendererConfig {
  fillOpacity: string; // Hex opacity suffix (e.g., '33' for ~20%)
  borderLineWidth: number;
  borderLineDash: number[];
  // Center label config
  showCenterLabel: boolean;
  maxScaleForLabel: number; // Maximum scale to show labels - hide when zoomed in more (e.g., 0.3)
  centerLabelFormat: (area: AreaBoundary) => string;
  centerLabelPadding: number;
  // Hover config
  hoverBorderLineWidth: number;
}

const defaultConfig: AreaRendererConfig = {
  fillOpacity: '33',
  borderLineWidth: 1,
  borderLineDash: [4, 4],
  showCenterLabel: true,
  maxScaleForLabel: 0.3,
  centerLabelFormat: (area) => `Area: ${area.name}`,
  centerLabelPadding: 4,
  hoverBorderLineWidth: 2,
};

export class AreaRenderer extends BaseRenderer<AreaRendererConfig> {
  /**
   * Calculate area screen size (in pixels)
   */
  private calculateAreaScreenSize(
    area: AreaBoundary,
    worldToScreen: (x: number, y: number) => { x: number; y: number }
  ): { width: number; height: number } {
    const screenPoints = area.polygon.map(p => worldToScreen(p.x, p.y));

    const minX = Math.min(...screenPoints.map(p => p.x));
    const maxX = Math.max(...screenPoints.map(p => p.x));
    const minY = Math.min(...screenPoints.map(p => p.y));
    const maxY = Math.max(...screenPoints.map(p => p.y));

    return {
      width: maxX - minX,
      height: maxY - minY
    };
  }

  /**
   * Calculate optimal font size that fits inside area
   */
  private calculateOptimalFontSize(
    labelText: string,
    areaWidth: number,
    areaHeight: number,
    ctx: CanvasRenderingContext2D
  ): number {
    const fontFamily = this.theme.typography.fontFamily;
    // Compress the dynamic range so small areas do not get tiny labels
    // and large areas do not get overly dominant ones.
    const rawFontSize = Math.min(areaWidth * 0.09, areaHeight * 0.22, 64);
    let fontSize = Math.min(26, 12 + Math.sqrt(Math.max(rawFontSize - 12, 0)) * 2);

    ctx.font = `bold ${fontSize}px ${fontFamily}`;
    let metrics = ctx.measureText(labelText);

    // Reduce font size until text fits with padding
    while (fontSize > 12 && (metrics.width > areaWidth * 0.65 || fontSize > areaHeight * 0.3)) {
      fontSize -= 2;
      ctx.font = `bold ${fontSize}px ${fontFamily}`;
      metrics = ctx.measureText(labelText);
    }

    return Math.max(fontSize, 12);
  }

  constructor(theme: Theme, config?: Partial<AreaRendererConfig>) {
    super(theme, defaultConfig, config);
  }

  render(
    ctx: CanvasRenderingContext2D,
    areas: AreaBoundary[],
    viewState: ViewState,
    worldToScreen: (x: number, y: number) => { x: number; y: number },
    hoveredArea: AreaBoundary | null
  ): void {

    const {
      fillOpacity,
      borderLineWidth,
      borderLineDash,
      showCenterLabel,
      maxScaleForLabel,
      centerLabelFormat,
      centerLabelPadding,
      hoverBorderLineWidth,
    } = this.config;

    areas.forEach((area) => {
      if (area.polygon.length < 2) return;

      // Convert world coordinates to screen coordinates
      const screenPoints = area.polygon.map(point => worldToScreen(point.x, point.y));

      // Determine styling
      const isHovered = hoveredArea === area;

      // Set line dash before drawing
      ctx.setLineDash(borderLineDash);

      // Draw polygon with fill and border
      drawPolygon(ctx, screenPoints, {
        fill: true,
        stroke: true,
        closePath: true,
        style: {
          fillStyle: area.color + fillOpacity,
          strokeStyle: isHovered ? this.theme.palette.storageUi.highlight : area.borderColor,
          lineWidth: isHovered ? hoverBorderLineWidth : borderLineWidth
        }
      });

      // Reset line dash
      ctx.setLineDash([]);

      // Center label logic - show only when zoomed out (scale below threshold)
      if (showCenterLabel && viewState.scale < maxScaleForLabel) {
        const centroid = calculatePolygonCentroid(area.polygon);
        const centerPos = worldToScreen(centroid.x, centroid.y);
        const labelText = centerLabelFormat(area);

        // Calculate area screen size for font sizing
        const areaScreenSize = this.calculateAreaScreenSize(area, worldToScreen);

        // Calculate optimal font size that fits inside area
        const fontSize = this.calculateOptimalFontSize(
          labelText,
          areaScreenSize.width,
          areaScreenSize.height,
          ctx
        );

        drawTextWithBackground(ctx, labelText, centerPos.x, centerPos.y, {
          font: `bold ${fontSize}px sans-serif`,
          textColor: this.theme.palette.storageUi.textPrimary + '66',
          backgroundColor: this.theme.palette.storageUi.backgroundSecondary + '99',
          padding: centerLabelPadding,
          textAlign: 'center',
          textBaseline: 'middle'
        });
      }
    });
  }
}
